import { Pagination } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'
import styled from 'styled-components'
import { Image } from '@/features/shared/elements/Image/NextImage'
import { type Product } from '@/features/shop/services/Product/types'
import { ThumbsCarousel } from '@/features/shop/components/Carousel/ThumbsCarousel'
import { Box } from '@shipt/design-system-layouts'

type Props = {
  product: Product
}

export const ProductImageCarousel = ({ product }: Props) => {
  const { secondaryImages, image, oos = false, name, size } = product
  const { original_size_url, url } = image

  const slideImages: string[] =
    secondaryImages.length > 0
      ? [original_size_url ?? url, ...secondaryImages]
      : [original_size_url ?? url]

  const preloadMainPdpImageByIndex = (index: number) => {
    const imageToPreload = slideImages[index]
    if (imageToPreload) new window.Image().src = imageToPreload
  }

  return (
    <>
      <Box display={{ md: 'none' }}>
        <ImageSwiper
          modules={[Pagination]}
          grabCursor
          pagination={{
            el: '.swiper-pagination',
            clickable: true,
          }}
        >
          {slideImages.map((src, i) => (
            <SwiperSlide key={src} className="swiper-slide">
              {({ isActive }) =>
                (isActive || i === 0) && (
                  <ProductImage
                    src={src}
                    alt={`slide ${i + 1} of ${slideImages.length}, ${name}, ${size}`}
                    $oos={oos}
                    fill
                    style={{ objectFit: 'contain' }}
                    unoptimized
                    preload={i === 0}
                    onLoad={() => preloadMainPdpImageByIndex(i + 1)}
                  />
                )
              }
            </SwiperSlide>
          ))}
          <div className="swiper-pagination" />
        </ImageSwiper>
      </Box>
      <Box display={{ base: 'none', md: 'block' }}>
        <ThumbsCarousel
          imageOpacity={oos ? '50%' : '100%'}
          slideImages={slideImages}
          productName={name}
          productSize={size}
        />
      </Box>
    </>
  )
}

const ImageSwiper = styled(Swiper)`
  padding-bottom: 1.5rem;
  --swiper-pagination-bottom: 0;
  --swiper-pagination-color: ${({ theme }) => theme.gray900};

  .swiper-slide {
    width: 200px;
    height: 200px;
  }
`

const ProductImage = styled(Image)<{ $oos: boolean }>`
  opacity: ${({ $oos }) => ($oos ? '50%' : '100%')};
`
